import { Entity, IClientTarget, ISiteTarget, ITenantTarget } from "./generics";
import { IFieldGroup, IFieldGroupBuilt, IFieldGroupReference } from "./object/field-group";
import { ISection, SLA } from "./object/object";
import { AttachmentProperties, CommentProperties, ICommentEntity, IFileReference } from "./objects";
import { EntityInstanceLink } from "./instance-linking";
import { QueueProperties } from "./queue";
import { SchemaReference } from "./schema";
import { UserReferenceEntity } from "./users";
import { NotificationSchema } from "./notification";
import { StateAssigneeConfig, WorkflowProperties } from "./workflow";
import { DistributedEntity } from "./distributed-entity";
import { WebhookConfig } from "./webhook";
import { isObject } from "../utilities";
import type { RoutingObservationRecord } from "./workflow-routing";

// oxlint-disable-next-line no-shadow
export enum TrainingType {
  "training" = "training",
  "competencies" = "competencies",
  "certification" = "certification",
}

// oxlint-disable-next-line no-shadow
export enum TrainingRoleType {
  "recommended" = "recommended",
  "mandatory" = "mandatory",
}

export type TrainingTemplateConfigRef = {
  schemaId: string;
  schemaVersion?: number;
};

export interface TrainingConfig<G = IFieldGroup | IFieldGroupReference>
  extends TrainingBase, Omit<WorkflowProperties, "state" | "routingObservations"> {
  hasEndDate?: boolean;

  // SLA (dates)
  slaSchema?: Record<string, SLA>;
  datesSchema?: unknown;

  // section
  sections: ISection<G>;

  // TODO: this could be replaced with an something like "automation rules", that jira has.
  // track what auto-assignment based of form state
  // - this need to handle supervisor re-assignment
  // - this needs to preserve previous assignee state so that reversion will work
  // - on initial creation set first state uids to be assignees passed in from form, or the created by user
  // - what should adding assignee in the middle of state do? it should update the assignees map. not the stateAssigneeMap.
  //          unless your editing the object while its in a state with `assignee` true.
  //          then all states with `assignee` will be synced

  stateAssigneeMap?: {
    // auto assignee to these people when state is transitioned to this `objectState`.
    // assignee: true => editing activeUsers will update assignees
    // supervisor: true => assign to supervisors of the last state that was configured with assignee boolean

    [objectState: string]: undefined | StateAssigneeConfig;
  };

  /**
   * section ordering is done by sectionIdx
   */
  ordering?: string[];
}

/**
 * Training config that are backed by a training template
 */
export interface TrainingConfigWithReference extends TrainingBase {
  /**
   * Local expiration setting for a template-backed training config. When this is
   * undefined the training inherits the template expiration setting.
   */
  hasEndDate?: boolean;

  /**
   * references a schema that is a training config and that schema has isTemplate
   */
  templateTrainingConfigRef: TrainingTemplateConfigRef;
}

interface TrainingBase extends CommentProperties, AttachmentProperties {
  name: string;
  type: TrainingType;
  sharable?: boolean;

  metadata?: {
    description?: string;
    estimatedCost?: number;
    estimatedHours?: number;
    trainingCategory?: string;
    [props: string]: unknown;
  };

  notificationSchemaRef?: {
    schemaId: string;
    schemaVersion: number;
  };

  notificationSchema?: NotificationSchema;
  webhookConfigs?: WebhookConfig[] | null;

  counterRef?: {
    counterConfigId: string;
    distributor?: DistributedEntity["distributor"];
  } | null;
}

export type AllTrainingReferenceMetadata = Pick<
  TrainingConfig,
  "name" | "type" | "hasEndDate" | "sharable" | "metadata"
> & { templateTrainingConfigRef?: TrainingConfigWithReference["templateTrainingConfigRef"] };

export type AllTrainingReference = SchemaReference<
  AllTrainingReferenceMetadata,
  AllTrainingReferenceMetadata
>;
export type AllTrainingRecord = Record<string, AllTrainingReference>;

export type TrainingInstance<
  T = Record<string, unknown>,
  G = IFieldGroupBuilt,
> = TrainingConfig<G> &
  QueueProperties &
  Entity & {
    /**
     * who's to-do list this will show up in
     * - this can be computed based on the state and the assigneesMap but
     */
    activeUsers?: Record<string, UserReferenceEntity>;

    owners: Record<string, UserReferenceEntity>;

    /**
     * This is a map of user assignee references based on state,
     * {
     *   [ stateKey ] : {
     *      [ userId ]: UserRef
     *   }
     * }
     */
    assigneesByStateMap: Record<string, Record<string, UserReferenceEntity>>;

    /**
     * This is a map of queue assignees based on state.
     */
    queueIdsByStateMap?: Record<string, string[]>;

    /**
     * @deprecated
     * TODO: this can be renamed to "ownerIds" to represent owner of the item (this is user in training and CA?)
     * TRANSFORM: (rename) -> ownerId
     *
     * contextually who's responsible for this items
     */
    assignees?: Record<string, UserReferenceEntity>;

    /**
     * @deprecated
     * TODO: I think this can be removed from the system
     * TRANSFORM: (delete)
     *
     * who is responsible for managing it
     */
    reporters?: Record<string, UserReferenceEntity>;

    /**
     * who wants to get the 'watchers' notification
     */
    watcherIds?: string[];

    createdBy?: {
      uid?: string;
    };

    missing?: boolean;
    // this is only used while the training is actively missing.
    // Once its been updated this prop is removed. It will likely be out of date eventually
    requirementRefs?: Record<string, { schemaId: string; requirement: TrainingRoleType }>;

    state: string; // workflow property thats omitted in the TrainingConfig types
    routingObservations?: RoutingObservationRecord; // routing observations created for each instance

    data: T;
    links: Record<string, EntityInstanceLink>;
    schemaRef: {
      schemaId: string;
      schemaVersion: number;
    };

    webhookConfigs?: WebhookConfig[] | null;

    /**
     * Counter ref is stored in object config
     * counterId is new to thi instance defined on creation from the checkout counter utility
     */
    counterId?: string;
    counterRef?: {
      counterConfigId: string;
      distributor?: DistributedEntity["distributor"];
    } | null;
  };

export type ElasticTrainingInstanceEntity<T = unknown> = Omit<
  TrainingInstance<T>,
  | "owners"
  | "assigneesByStateMap"
  | "assignees"
  | "reporters"
  | "links"
  | "attachments"
  | "comments"
  | "activeUsers"
  | "requirementRefs"
> & {
  requirementRefs: { schemaId: string; requirement: TrainingRoleType }[];
  owners: UserReferenceEntity[];
  assignedUserIds: string[];
  assigneesByStateMap: Record<string, UserReferenceEntity[]>;
  activeUsers: UserReferenceEntity[];
  attachments: IFileReference[];
  comments: ICommentEntity[];
  links: EntityInstanceLink[];
  searchText: string[];
  /** @deprecated */
  reporters: UserReferenceEntity[];
  /** @deprecated */
  assignees: UserReferenceEntity[];
};

export type TrainingInstanceReference<T = number> = {
  id: string;
  trainingId: string;
  state: string;
  endAt?: T | null;
  startAt?: T | null;
  createdAt: T;
  updatedAt?: T;
  missing?: boolean;
  isResolved?: boolean;
  activeUserIds?: string[];
  owners?: string[];
  queueIds?: string[];
  externalSource?: IClientTarget | ISiteTarget | ITenantTarget;
};

export type TrainingReference = {
  id: string;
  name: string;
  requirement: TrainingRoleType;
};

export function isTrainingReference(training: unknown): training is TrainingReference {
  return (
    typeof training === "object" &&
    Object.getOwnPropertyNames(training).includes("id") &&
    Object.getOwnPropertyNames(training).includes("name") &&
    Object.getOwnPropertyNames(training).includes("requirement")
  );
}

export function hasTrainingTemplateConfigRef(
  training: unknown,
): training is { templateTrainingConfigRef: TrainingTemplateConfigRef } {
  return (
    typeof training === "object" &&
    Boolean(training) &&
    Object.getOwnPropertyNames(training).includes("templateTrainingConfigRef")
  );
}

export function isTrainingConfigWithReference(
  training: unknown,
): training is TrainingConfigWithReference {
  return (
    typeof training === "object" &&
    Boolean(training) &&
    Object.getOwnPropertyNames(training).includes("name") &&
    Object.getOwnPropertyNames(training).includes("type") &&
    hasTrainingTemplateConfigRef(training) &&
    !Object.getOwnPropertyNames(training).includes("sections")
  );
}

export function hasTrainingConfigProps(
  training: TrainingConfig | TrainingConfigWithReference,
): training is TrainingConfig;
export function hasTrainingConfigProps(
  training: Partial<TrainingConfig | TrainingConfigWithReference>,
): training is Partial<TrainingConfig>;
export function hasTrainingConfigProps(
  training:
    | TrainingConfig
    | TrainingConfigWithReference
    | Partial<TrainingConfig | TrainingConfigWithReference>,
): training is Partial<TrainingConfig> | TrainingConfig;
export function hasTrainingConfigProps(
  training:
    | TrainingConfig
    | TrainingConfigWithReference
    | Partial<TrainingConfig | TrainingConfigWithReference>,
): training is Partial<TrainingConfig> | TrainingConfig {
  return (
    isObject(training) &&
    (Object.hasOwn(training, "sections" satisfies keyof TrainingConfig) ||
      Object.hasOwn(training, "workflow" satisfies keyof TrainingConfig) ||
      Object.hasOwn(training, "hasEndDate" satisfies keyof TrainingConfig) ||
      Object.hasOwn(training, "stateAssigneeMap" satisfies keyof TrainingConfig) ||
      Object.hasOwn(training, "ordering" satisfies keyof TrainingConfig) ||
      Object.hasOwn(training, "slaSchema" satisfies keyof TrainingConfig) ||
      Object.hasOwn(training, "datesSchema" satisfies keyof TrainingConfig))
  );
}

export function isTraining<T>(training: unknown): training is TrainingConfig<T> {
  const propsNames = training ? Object.getOwnPropertyNames(training) : [];
  
  return (
    typeof training === "object" &&
    propsNames.includes("name") &&
    (propsNames.includes("sections") || propsNames.includes("templateTrainingConfigRef"))&&
    propsNames.includes("type") &&
    [
      TrainingType.certification,
      TrainingType.competencies,
      TrainingType.training,
      // oxlint-disable-next-line typescript/no-explicit-any
    ].includes((training as any).type)
  );
}

export function isTrainingInstance(training: unknown): training is TrainingInstance {
  const propsNames = training ? Object.getOwnPropertyNames(training) : [];
  return (
    typeof training === "object" &&
    propsNames.includes("name") &&
    propsNames.includes("sections") &&
    propsNames.includes("type") &&
    propsNames.includes("state") &&
    [
      TrainingType.certification,
      TrainingType.competencies,
      TrainingType.training,
      // oxlint-disable-next-line typescript/no-explicit-any
    ].includes((training as any).type)
  );
}
